import React, { useState, useEffect, forwardRef,
  useImperativeHandle } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import { useTranslation } from 'react-i18next';
import IdCard from "../../../../../../../../assets/images/Vector.svg";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent, {
  personNameFloatingKeyboardProps,
} from "../../../common/FloatingTextInputComponent";
import {
  isPersonNameSegmentValid,
  sanitizePersonNameInput,
  RECEIVER_FIRST_NAME_MAX_LENGTH,
  RECEIVER_LAST_NAME_MAX_LENGTH,
  RECEIVER_MIDDLE_NAME_MAX_LENGTH,
} from "../../../../../helper/personNameValidation";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  /**
   * Triggered when the user taps the inline Continue button at the bottom of the form.
   * Wired by MainSendCashScreen to its `handleNext`, which already runs the imperative
   * `submit()` validator on this step before advancing.
   */
  onContinue?: () => void;
  testID?: string | undefined;
};

export interface ReceiverFullNameStepHandle {
  submit: () => Promise<boolean>;
}

const ReceiverFullNameStep = forwardRef<ReceiverFullNameStepHandle, Props>(({ onValidationChange, onContinue, testID }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    lastName2: "",
  });
  const gffpFieldsByCategory = useSessionStore((s: SessionState) => (s as any).gffpFieldsByCategory);
  const destinationInfoFields: any[] = (gffpFieldsByCategory?.["Destination Information"]) || [];
  const getFieldLabel = (tag: string): string | undefined => {
    const field = destinationInfoFields.find((f: any) => (f.XmlTag ?? f.xmlTag) === tag);
    const baseLabel = field?.FieldLabel ?? field?.fieldLabel;
    const visibility = field?.Visibility ?? field?.visibility;
    const isOptional = visibility === 1 || visibility === "OPT" || visibility === "1";
    return baseLabel ? `${baseLabel}${isOptional ? " (optional)" : ""}` : undefined;
  };

  // Bind to store
  const setFirst = useSessionStore((s: any) => s.setReceiverFirstName);
  const setMiddle = useSessionStore((s: any) => s.setReceiverMiddleName);
  const setLast = useSessionStore((s: any) => s.setReceiverLastName);
  const setLast2 = useSessionStore((s: any) => s.setReceiverLastName2);

  const [firstName, setFirstName] = useState(() =>
    sanitizePersonNameInput(
      String((useSessionStore.getState() as any).receiverFirstName ?? ""),
      RECEIVER_FIRST_NAME_MAX_LENGTH
    )
  );
  const [middleName, setMiddleName] = useState(() =>
    sanitizePersonNameInput(
      String((useSessionStore.getState() as any).receiverMiddleName ?? ""),
      RECEIVER_MIDDLE_NAME_MAX_LENGTH
    )
  );
  const [lastName, setLastName] = useState(() =>
    sanitizePersonNameInput(
      String((useSessionStore.getState() as any).receiverLastName ?? ""),
      RECEIVER_LAST_NAME_MAX_LENGTH
    )
  );
  const [lastName2, setLastName2] = useState(() =>
    sanitizePersonNameInput(
      String((useSessionStore.getState() as any).receiverLastName2 ?? ""),
      RECEIVER_LAST_NAME_MAX_LENGTH
    )
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => { setFirst?.(firstName || null); }, [firstName]);
  useEffect(() => { setMiddle?.(middleName || null); }, [middleName]);
  useEffect(() => { setLast?.(lastName || null); }, [lastName]);
  useEffect(() => { setLast2?.(lastName2 || null); }, [lastName2]);

  const isValid =
    isPersonNameSegmentValid(firstName, true, RECEIVER_FIRST_NAME_MAX_LENGTH) &&
    isPersonNameSegmentValid(middleName, false, RECEIVER_MIDDLE_NAME_MAX_LENGTH) &&
    isPersonNameSegmentValid(lastName, true, RECEIVER_LAST_NAME_MAX_LENGTH) &&
    isPersonNameSegmentValid(lastName2, false, RECEIVER_LAST_NAME_MAX_LENGTH);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
  useImperativeHandle(ref, () => ({
    submit: async () => {
      const firstErr = !firstName.trim()
        ? t("PERSON_NAME_ERROR_FIRST_REQUIRED")
        : firstName.length > RECEIVER_FIRST_NAME_MAX_LENGTH
          ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: RECEIVER_FIRST_NAME_MAX_LENGTH })
          : !isPersonNameSegmentValid(firstName, true)
            ? t("PERSON_NAME_ERROR_LETTERS_ONLY")
            : "";
      const lastErr = !lastName.trim()
        ? t("PERSON_NAME_ERROR_LAST_REQUIRED")
        : lastName.length > RECEIVER_LAST_NAME_MAX_LENGTH
          ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: RECEIVER_LAST_NAME_MAX_LENGTH })
          : !isPersonNameSegmentValid(lastName, true)
            ? t("PERSON_NAME_ERROR_LETTERS_ONLY")
            : "";
      const middleErr =
        middleName.trim() &&
        (middleName.length > RECEIVER_MIDDLE_NAME_MAX_LENGTH
          ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: RECEIVER_MIDDLE_NAME_MAX_LENGTH })
          : !isPersonNameSegmentValid(middleName, false)
            ? t("PERSON_NAME_ERROR_LETTERS_ONLY")
            : "");
      const last2Err =
        lastName2.trim() &&
        (lastName2.length > RECEIVER_LAST_NAME_MAX_LENGTH
          ? t("PERSON_NAME_ERROR_MAX_CHARS", { count: RECEIVER_LAST_NAME_MAX_LENGTH })
          : !isPersonNameSegmentValid(lastName2, false)
            ? t("PERSON_NAME_ERROR_LETTERS_ONLY")
            : "");
      const newErrors = {
        firstName: firstErr,
        middleName: middleErr,
        lastName: lastErr,
        lastName2: last2Err,
      };
      setErrors(newErrors);

      return !Object.values(newErrors).some(Boolean);
    },
  }));

  return (
    <View
      testID={testID}
      style={styles.container}
    >
      <View style={styles.headerSection}>
        <Text style={styles.title}>{t("TELL_US_ABOUT_RECEIVER")}</Text>
        <Text style={styles.subtitle}>{t("PROVIDE_DETAILS_ABOUT_YOUR_RECEIVER")}</Text>
      </View>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        
      <FloatingTextInputComponent
        {...personNameFloatingKeyboardProps}
        testID="receiverFirstName"
        label= {t("FIRST_NAME")}//{getFieldLabel("receiverFirstName") || t("FIRST_NAME")}
        value={firstName}
        isAutoFocus={false}
        maxLength={RECEIVER_FIRST_NAME_MAX_LENGTH}
        onChangeText={(val) =>
          setFirstName(sanitizePersonNameInput(val, RECEIVER_FIRST_NAME_MAX_LENGTH))
        }
        errorText={errors.firstName}
        onClearError={() => setErrors((prev) => ({ ...prev, firstName: "" }))}
        blockPersonNameAutofill
      />
      <FloatingTextInputComponent
        {...personNameFloatingKeyboardProps}
        testID="receiverMiddleName"
        label={t("MIDDLE_NAME")} // getFieldLabel("receiverMiddleName") || 
        value={middleName}
        maxLength={RECEIVER_MIDDLE_NAME_MAX_LENGTH}
        onChangeText={(v) =>
          setMiddleName(sanitizePersonNameInput(v, RECEIVER_MIDDLE_NAME_MAX_LENGTH))
        }
        errorText={errors.middleName}
        onClearError={() => setErrors((prev) => ({ ...prev, middleName: "" }))}
        blockPersonNameAutofill
      />
      <FloatingTextInputComponent
        {...personNameFloatingKeyboardProps}
        testID="receiverLastName"
        label={t("LAST_NAME")} //getFieldLabel("receiverLastName") || 
        value={lastName}
        maxLength={RECEIVER_LAST_NAME_MAX_LENGTH}
        onChangeText={(val) =>
          setLastName(sanitizePersonNameInput(val, RECEIVER_LAST_NAME_MAX_LENGTH))
        }
        errorText={errors.lastName}
        onClearError={() => setErrors((prev) => ({ ...prev, lastName: "" }))}
        blockPersonNameAutofill
      />
      <FloatingTextInputComponent
        {...personNameFloatingKeyboardProps}
        testID="receiverLastName2"
        label={t("NICKNAME")} //getFieldLabel("receiverLastName2") ||
        value={lastName2}
        maxLength={RECEIVER_LAST_NAME_MAX_LENGTH}
        onChangeText={(v) =>
          setLastName2(sanitizePersonNameInput(v, RECEIVER_LAST_NAME_MAX_LENGTH))
        }
        errorText={errors.lastName2}
        onClearError={() => setErrors((prev) => ({ ...prev, lastName2: "" }))}
        blockPersonNameAutofill
      />
      <View style={styles.hintBox}>
        <IdCard width={24} height={24}/>
        <Text style={styles.hintText}>
          {t("YOUR_RECEIVER_NAME_MUST_MATCH")}
        </Text>
      </View>
      {/* Pushes Continue to the bottom of the form when there's leftover space
          (no keyboard). When the keyboard shrinks the ScrollView and content
          overflows, this spacer collapses to 0 and the user scrolls instead. */}
      <View style={styles.scrollSpacer} />
      <TouchableOpacity
        testID="continueButton"
        style={styles.continueButton}
        onPress={() => onContinue?.()}
      >
        <Text style={styles.continueButtonText}>{t("STRKEY_CONTINUE")}</Text>
      </TouchableOpacity>
      </ScrollView>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  headerSection: {
    flexShrink: 0,
    width: "100%",
  },
  /** Fills remaining height in stepAreaContainer; scrolls fields so nothing is clipped (esp. web overflow:hidden). */
  scroll: {
    flex: 1,
    width: "100%",
    ...(Platform.OS === "web" && { minHeight: 0 as any }),
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  input: {
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 16,
    marginBottom: 14,
    color: "#737373"
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  bottomSheet: {
    alignItems: 'center',
    backgroundColor: "#fff",
    padding: 20,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 20
  },
  modalText: {
    fontSize: 15,
    color: "#555",
    fontWeight: "400",
    width: 311,
    paddingHorizontal: 15,
    paddingBottom: 20,
    textAlign: 'center'
  },
  modalDOB: {
    fontSize: 32,
    fontWeight: "500",
    marginBottom: 20,
    textAlign: "center",
  },
  confirmBtn: {
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
    width: 350
  },
  confirmText: {
    color: "#fff",
    fontWeight: "600",
    fontSize: 16
  },
  cancelBtn: {
    marginTop: 20,
    alignItems: "center"
  },
  cancelText: {
    fontSize: 15,
    color: "#666"
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 30,
    paddingTop: 80,
    paddingBottom: 50,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#E3F2FD',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#2196F3',
  },
  iconText: {
    fontSize: 32,
  },
  hintBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: Colors.titleTextColor,
    borderRadius: 10,
    padding: 16,
    marginTop: 32,
    backgroundColor: Colors.white,
    marginBottom: 24,
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 14,
    fontWeight: '400', 
    color: Colors.darkGrey,
    lineHeight: 18,
    paddingLeft: 16
  },
  /**
   * Continue button rendered inside the inner ScrollView so the user must scroll past
   * the four name fields and the legal-ID hint to reach it. This intentionally avoids
   * a sticky footer above the keyboard for this step.
   */
  scrollSpacer: {
    flexGrow: 1,
  },
  continueButton: {
    backgroundColor: Colors.black,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 0,
  },
  continueButtonText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '600',
    color: Colors.white,
    lineHeight: 20,
  },
});

export default ReceiverFullNameStep